// AccountsChart.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
  AccountTree as AccountTreeIcon,
  AccountBalanceWallet as LedgerIcon,
  Search as SearchIcon,
  LockOutlined as LockIcon,
} from "@mui/icons-material";
import { useAppContext } from "../../context/AppContext";
import axiosInstance from "../../api/axiosInstance";

// ------------------------------------------------------------
// LIGHT BLUE THEME TOKENS (normal font weights only)
// ------------------------------------------------------------
const C = {
  primary: "#2b7bc9",
  primaryHover: "#2369ab",
  heading: "#24527f",
  text: "#2d3b4e",
  muted: "#6b7c93",
  page: "#f6f9fc",
  card: "#ffffff",
  soft: "#eef5fc",
  selected: "#e1eefb",
  border: "#dbe7f4",
  drBg: "#e8f1fb",
  drText: "#2369ab",
  crBg: "#fdf1e3",
  crText: "#a85a05",
};

const W = { normal: 400, medium: 500 };

const primaryBtn = {
  bgcolor: C.primary,
  color: "#fff",
  fontWeight: W.medium,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: C.primaryHover, boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#b9d3ec", color: "#fff" },
};

const outlineBtn = {
  color: C.primary,
  borderColor: C.border,
  fontWeight: W.medium,
  textTransform: "none",
  bgcolor: C.card,
  "&:hover": { borderColor: C.primary, bgcolor: C.soft },
  "&.Mui-disabled": { color: "#a9bdd3", borderColor: C.border },
};

const headCell = {
  bgcolor: C.soft,
  color: C.heading,
  fontWeight: W.medium,
  fontSize: 13,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${C.border}`,
};

// Requirement: IDs 1-30 are default groups; IDs 1-100 are default ledgers.
const isDefaultGroup = (group) => {
  const id = Number(group?.id);
  return Number.isInteger(id) && id >= 1 && id <= 30;
};

const isDefaultLedger = (ledger) => {
  const id = Number(ledger?.id);
  return Number.isInteger(id) && id >= 1 && id <= 100;
};

const emptyGroup = { name: "", parent_group_id: "", description: "" };
const emptyLedger = {
  name: "",
  group_id: "",
  description: "",
  opening_balance: "0",
  opening_balance_type: "DR",
  opening_balance_date: "",
  balance_type: "BALANCE_ONLY",
};

const BALANCE_TYPES = [
  { value: "BALANCE_ONLY", label: "Balances Only" },
  { value: "ON_ACCOUNT", label: "On Account" },
];

// ---- Date helpers: the form shows dd/mm/yyyy, the API uses yyyy-mm-dd ----
const isoToDmy = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

const dmyToIso = (value) => {
  const match = String(value || "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  const valid =
    date.getFullYear() === Number(yyyy) &&
    date.getMonth() === Number(mm) - 1 &&
    date.getDate() === Number(dd);
  return valid ? `${yyyy}-${mm}-${dd}` : null;
};

// Adds the slashes while typing: 01042026 -> 01/04/2026
const maskDmy = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isCredit = (value) => String(value || "DR").toUpperCase() === "CR";

function DrCrChip({ value }) {
  const credit = isCredit(value);
  return (
    <Chip
      label={credit ? "Cr" : "Dr"}
      size="small"
      sx={{
        height: 22,
        minWidth: 36,
        fontSize: 12,
        fontWeight: W.medium,
        color: credit ? C.crText : C.drText,
        bgcolor: credit ? C.crBg : C.drBg,
      }}
    />
  );
}

export default function AccountsChart() {
  const { clientId } = useAppContext();

  const [groups, setGroups] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(emptyGroup);
  const [notice, setNotice] = useState({
    open: false,
    severity: "success",
    text: "",
  });
  const [search, setSearch] = useState("");

  const showNotice = (text, severity = "success") =>
    setNotice({ open: true, severity, text });

  const loadData = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/api/accounts-chart/data", {
        params: { client_id: clientId },
      });
      const nextGroups = Array.isArray(data?.groups) ? data.groups : [];
      const nextLedgers = Array.isArray(data?.ledgers) ? data.ledgers : [];
      setGroups(nextGroups);
      setLedgers(nextLedgers);

      setSelectedGroupId((oldId) => {
        if (oldId && nextGroups.some((g) => Number(g.id) === Number(oldId)))
          return oldId;
        return nextGroups.length ? nextGroups[0].id : null;
      });
    } catch (error) {
      console.error("Accounts Chart load failed:", error);
      showNotice(
        error.response?.data?.message || "Could not load Accounts Chart.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedGroup =
    groups.find((g) => Number(g.id) === Number(selectedGroupId)) || null;

  const groupsByParent = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => {
      const key =
        group.parent_group_id == null ? "root" : String(group.parent_group_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(group);
    });
    for (const list of map.values())
      list.sort((a, b) => a.name.localeCompare(b.name));
    return map;
  }, [groups]);

  const groupLedgerCounts = useMemo(() => {
    const counts = new Map();
    ledgers.forEach((ledger) => {
      counts.set(
        Number(ledger.group_id),
        (counts.get(Number(ledger.group_id)) || 0) + 1,
      );
    });
    return counts;
  }, [ledgers]);

  const descendantsOf = (groupId) => {
    const result = [];
    const walk = (id) => {
      (groupsByParent.get(String(id)) || []).forEach((child) => {
        result.push(child);
        walk(child.id);
      });
    };
    walk(groupId);
    return result;
  };

  const openGroupDialog = (mode, group = null, parentId = null) => {
    setDialog({ type: "group", mode, id: group?.id ?? null });
    setForm(
      group
        ? {
            name: group.name || "",
            parent_group_id: group.parent_group_id ?? "",
            description: group.description || "",
          }
        : { ...emptyGroup, parent_group_id: parentId ?? "" },
    );
  };

  const openLedgerDialog = (mode, ledger = null) => {
    setDialog({ type: "ledger", mode, id: ledger?.id ?? null });
    setForm(
      ledger
        ? {
            name: ledger.name || "",
            group_id: String(ledger.group_id ?? selectedGroupId ?? ""),
            description: ledger.description || "",
            opening_balance: String(ledger.opening_balance ?? 0),
            opening_balance_type: ledger.opening_balance_type || "DR",
            opening_balance_date: isoToDmy(ledger.opening_balance_date),
            balance_type:
              ledger.balance_type === "ON_ACCOUNT"
                ? "ON_ACCOUNT"
                : "BALANCE_ONLY",
          }
        : { ...emptyLedger, group_id: String(selectedGroupId ?? "") },
    );
  };

  const closeDialog = () => {
    if (!saving) setDialog(null);
  };

  const saveGroup = async () => {
    const payload = {
      client_id: clientId,
      name: form.name.trim(),
      parent_group_id: form.parent_group_id || null,
      description: form.description,
    };
    setSaving(true);
    try {
      if (dialog.mode === "edit") {
        await axiosInstance.put(
          `/api/accounts-chart/groups/${dialog.id}`,
          payload,
        );
      } else {
        await axiosInstance.post("/api/accounts-chart/groups", payload);
      }
      setDialog(null);
      await loadData();
      showNotice(dialog.mode === "edit" ? "Group updated." : "Group added.");
    } catch (error) {
      showNotice(
        error.response?.data?.message || "Could not save group.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const saveLedger = async () => {
    const dateText = String(form.opening_balance_date || "").trim();
    const isoDate = dateText ? dmyToIso(dateText) : null;
    if (dateText && !isoDate) {
      showNotice("Enter the date as dd/mm/yyyy, for example 31/03/2026.", "error");
      return;
    }

    const payload = {
      client_id: clientId,
      group_id: Number(form.group_id),
      name: form.name.trim(),
      description: form.description,
      opening_balance: Number(form.opening_balance || 0),
      opening_balance_type: form.opening_balance_type,
      opening_balance_date: isoDate,
      balance_type: form.balance_type || "BALANCE_ONLY",
    };
    setSaving(true);
    try {
      let response;
      if (dialog.mode === "edit") {
        response = await axiosInstance.put(
          `/api/accounts-chart/ledgers/${dialog.id}`,
          payload,
        );
      } else {
        response = await axiosInstance.post(
          "/api/accounts-chart/ledgers",
          payload,
        );
      }
      setDialog(null);
      await loadData();
      showNotice(
        response.data?.message ||
          (dialog.mode === "edit" ? "Ledger updated." : "Ledger added."),
      );
    } catch (error) {
      showNotice(
        error.response?.data?.message || "Could not save ledger.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (group) => {
    if (
      !window.confirm(
        `Delete group "${group.name}"? This is allowed only when it has no subgroups or ledgers.`,
      )
    )
      return;
    try {
      await axiosInstance.delete(`/api/accounts-chart/groups/${group.id}`, {
        params: { client_id: clientId },
      });
      await loadData();
      showNotice("Group deleted.");
    } catch (error) {
      showNotice(
        error.response?.data?.message || "Could not delete group.",
        "error",
      );
    }
  };

  const deleteLedger = async (ledger) => {
    if (
      !window.confirm(
        `Delete ledger "${ledger.name}"? It can be deleted only if no transactions refer to it.`,
      )
    )
      return;
    try {
      await axiosInstance.delete(`/api/accounts-chart/ledgers/${ledger.id}`, {
        params: { client_id: clientId },
      });
      await loadData();
      showNotice("Ledger deleted.");
    } catch (error) {
      showNotice(
        error.response?.data?.message || "Could not delete ledger.",
        "error",
      );
    }
  };

  // ------------------------------------------------------------
  // GROUP TREE
  // ------------------------------------------------------------
  const renderGroupTree = (parentId = null, depth = 0) => {
    const list =
      groupsByParent.get(parentId == null ? "root" : String(parentId)) || [];
    return list.map((group) => {
      const isSelected = Number(selectedGroupId) === Number(group.id);
      const hasChildren =
        (groupsByParent.get(String(group.id)) || []).length > 0;
      const locked = isDefaultGroup(group);
      return (
        <React.Fragment key={group.id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              pl: 1 + depth * 2.5,
              pr: 0.75,
              py: 0.4,
              mx: 0.75,
              my: 0.2,
              borderRadius: 1.5,
              bgcolor: isSelected ? C.selected : "transparent",
              borderLeft: `3px solid ${isSelected ? C.primary : "transparent"}`,
              "&:hover": { bgcolor: isSelected ? C.selected : C.soft },
            }}
          >
            <Box
              onClick={() => setSelectedGroupId(group.id)}
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                py: 0.4,
              }}
            >
              <AccountTreeIcon
                sx={{ fontSize: 18, color: locked ? "#9fb0c4" : C.primary }}
              />
              <Typography
                noWrap
                title={group.name}
                sx={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: isSelected ? W.medium : W.normal,
                  color: C.text,
                }}
              >
                {group.name}
              </Typography>
              <Chip
                label={groupLedgerCounts.get(Number(group.id)) || 0}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11.5,
                  fontWeight: W.normal,
                  color: C.heading,
                  bgcolor: isSelected ? C.card : C.soft,
                }}
              />
            </Box>
            {locked ? (
              <Tooltip title="Default group (protected)">
                <LockIcon sx={{ fontSize: 16, color: "#9fb0c4", mx: 0.5 }} />
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Add subgroup">
                  <IconButton
                    size="small"
                    sx={{ color: C.primary }}
                    onClick={() => openGroupDialog("add", null, group.id)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit group">
                  <IconButton
                    size="small"
                    sx={{ color: C.primary }}
                    onClick={() => openGroupDialog("edit", group)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete group">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteGroup(group)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          {hasChildren && renderGroupTree(group.id, depth + 1)}
        </React.Fragment>
      );
    });
  };

  const visibleLedgers = ledgers
    .filter((ledger) => Number(ledger.group_id) === Number(selectedGroupId))
    .filter((ledger) =>
      ledger.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

  const eligibleParentGroups = groups.filter((group) => {
    if (dialog?.type !== "group" || dialog.mode !== "edit") return true;
    if (Number(group.id) === Number(dialog.id)) return false;
    return !descendantsOf(dialog.id).some(
      (child) => Number(child.id) === Number(group.id),
    );
  });

  if (!clientId) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Your logged-in user does not have a client ID. Please sign in again or
        contact the administrator.
      </Alert>
    );
  }

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        bgcolor: C.page,
        minHeight: "100%",
        color: C.text,
      }}
    >
      {/* Page header: light tint, no heavy fill */}
      <Paper
        variant="outlined"
        sx={{
          mb: 2,
          px: { xs: 2, md: 2.5 },
          py: 2,
          borderRadius: 2.5,
          borderColor: C.border,
          bgcolor: C.soft,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 22, md: 26 },
                fontWeight: W.medium,
                color: C.heading,
                lineHeight: 1.2,
              }}
            >
              Accounts Chart
            </Typography>
            <Typography sx={{ fontSize: 14, color: C.muted, mt: 0.5 }}>
              Manage account groups and ledgers. Default accounts are protected.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              sx={outlineBtn}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => openGroupDialog("add")}
              sx={outlineBtn}
            >
              Add Group
            </Button>
            <Button
              variant="contained"
              startIcon={<LedgerIcon />}
              disabled={!selectedGroup}
              onClick={() => openLedgerDialog("add")}
              sx={primaryBtn}
            >
              Add Ledger
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress sx={{ color: C.primary }} size={40} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 30%) 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          {/* LEFT: groups */}
          <Paper
            variant="outlined"
            sx={{
              overflow: "hidden",
              borderRadius: 2.5,
              borderColor: C.border,
              bgcolor: C.card,
            }}
          >
            <Box sx={{ px: 2, py: 1.5, bgcolor: C.soft }}>
              <Typography
                sx={{ fontSize: 16, fontWeight: W.medium, color: C.heading }}
              >
                Account Groups
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                {groups.length} groups. The number beside each group is its
                ledger count.
              </Typography>
            </Box>
            <Divider sx={{ borderColor: C.border }} />
            <Box sx={{ maxHeight: { md: "70vh" }, overflow: "auto", py: 1 }}>
              {groups.length ? (
                renderGroupTree()
              ) : (
                <Typography sx={{ p: 2, color: C.muted }}>
                  No account groups found for this client. Use Add Group to
                  create one.
                </Typography>
              )}
            </Box>
          </Paper>

          {/* RIGHT: ledgers */}
          <Paper
            variant="outlined"
            sx={{
              overflow: "hidden",
              borderRadius: 2.5,
              borderColor: C.border,
              bgcolor: C.card,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                justifyContent: "space-between",
                bgcolor: C.soft,
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    sx={{ fontSize: 17, fontWeight: W.medium, color: C.heading }}
                  >
                    {selectedGroup?.name || "Select a group"}
                  </Typography>
                  {selectedGroup && isDefaultGroup(selectedGroup) && (
                    <Chip
                      icon={<LockIcon sx={{ fontSize: 14 }} />}
                      label="Default group (read-only)"
                      size="small"
                      sx={{
                        fontWeight: W.normal,
                        fontSize: 12,
                        bgcolor: C.card,
                        color: C.muted,
                        border: `1px solid ${C.border}`,
                        "& .MuiChip-icon": { color: C.muted },
                      }}
                    />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                  {visibleLedgers.length} ledger(s) shown
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search ledgers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  minWidth: { sm: 240 },
                  bgcolor: C.card,
                  borderRadius: 1,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: C.border,
                  },
                  "& input": { fontSize: 14, fontWeight: W.normal },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: C.primary, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider sx={{ borderColor: C.border }} />

            <TableContainer sx={{ maxHeight: { md: "65vh" } }}>
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: "fixed",
                  width: "100%",
                  minWidth: 760,
                  "& .MuiTableCell-root": {
                    px: 1.25,
                    py: 1,
                    fontSize: 13.5,
                    fontWeight: W.normal,
                    borderColor: "#e8f0f9",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCell, width: "30%" }}>
                      Ledger Name
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: "13%" }}>
                      Opening
                    </TableCell>
                    <TableCell align="center" sx={{ ...headCell, width: "7%" }}>
                      Dr/Cr
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: "13%" }}>
                      Current
                    </TableCell>
                    <TableCell align="center" sx={{ ...headCell, width: "7%" }}>
                      Dr/Cr
                    </TableCell>
                    <TableCell sx={{ ...headCell, width: "17%" }}>
                      Description
                    </TableCell>
                    <TableCell align="right" sx={{ ...headCell, width: "13%" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleLedgers.map((ledger) => {
                    const locked = isDefaultLedger(ledger);
                    return (
                      <TableRow
                        key={ledger.id}
                        hover
                        sx={{ "&:hover": { bgcolor: `${C.soft} !important` } }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ minWidth: 0 }}
                          >
                            <LedgerIcon
                              sx={{
                                fontSize: 18,
                                flexShrink: 0,
                                color: locked ? "#9fb0c4" : C.primary,
                              }}
                            />
                            <Typography
                              title={ledger.name}
                              sx={{
                                fontSize: 13.5,
                                fontWeight: W.medium,
                                minWidth: 0,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                lineHeight: 1.35,
                              }}
                            >
                              {ledger.name}
                            </Typography>
                            {locked && (
                              <Chip
                                label="Default"
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 19,
                                  fontSize: 10.5,
                                  flexShrink: 0,
                                  color: C.muted,
                                  borderColor: C.border,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatAmount(ledger.opening_balance)}
                        </TableCell>
                        <TableCell align="center">
                          <DrCrChip
                            value={ledger.opdrc || ledger.opening_balance_type}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: C.heading,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {formatAmount(
                            ledger.current_balance ?? ledger.opening_balance,
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <DrCrChip
                            value={
                              ledger.cldrc ||
                              ledger.opdrc ||
                              ledger.opening_balance_type
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            noWrap
                            title={ledger.description || ""}
                            sx={{ fontSize: 13, color: C.muted }}
                          >
                            {ledger.description || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {locked ? (
                            <Typography sx={{ fontSize: 12, color: C.muted }}>
                              Read-only
                            </Typography>
                          ) : (
                            <>
                              <Tooltip title="Edit ledger">
                                <IconButton
                                  size="small"
                                  sx={{ color: C.primary }}
                                  onClick={() =>
                                    openLedgerDialog("edit", ledger)
                                  }
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete ledger">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => deleteLedger(ledger)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!visibleLedgers.length && (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 6, color: C.muted, fontSize: 14 }}
                      >
                        {selectedGroup
                          ? "No ledgers in this group. Use Add Ledger to create one."
                          : "Select a group to view its ledgers."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Add / edit dialog */}
      <Dialog
        open={Boolean(dialog)}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2.5 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: C.soft,
            color: C.heading,
            fontWeight: W.medium,
            fontSize: 18,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          {dialog?.type === "group"
            ? `${dialog.mode === "edit" ? "Edit" : "Add"} Account Group`
            : `${dialog?.mode === "edit" ? "Edit" : "Add"} Ledger`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 3 }}>
            <TextField
              label={dialog?.type === "group" ? "Group Name" : "Ledger Name"}
              required
              autoFocus
              fullWidth
              value={form.name || ""}
              onChange={(e) =>
                setForm((old) => ({ ...old, name: e.target.value }))
              }
            />

            {dialog?.type === "group" ? (
              <FormControl fullWidth>
                <InputLabel>Parent Group (optional)</InputLabel>
                <Select
                  label="Parent Group (optional)"
                  value={
                    form.parent_group_id === null
                      ? ""
                      : String(form.parent_group_id ?? "")
                  }
                  onChange={(e) =>
                    setForm((old) => ({
                      ...old,
                      parent_group_id: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">No parent (top-level group)</MenuItem>
                  {eligibleParentGroups.map((group) => (
                    <MenuItem key={group.id} value={String(group.id)}>
                      {group.name}
                      {isDefaultGroup(group) ? " (Default)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth required>
                  <InputLabel>Account Group</InputLabel>
                  <Select
                    label="Account Group"
                    value={String(form.group_id || "")}
                    onChange={(e) =>
                      setForm((old) => ({ ...old, group_id: e.target.value }))
                    }
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.id} value={String(group.id)}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Opening Balance"
                    type="number"
                    fullWidth
                    inputProps={{ min: 0, step: "0.01" }}
                    value={form.opening_balance ?? "0"}
                    onChange={(e) =>
                      setForm((old) => ({
                        ...old,
                        opening_balance: e.target.value,
                      }))
                    }
                  />
                  <FormControl fullWidth>
                    <InputLabel>Dr/Cr</InputLabel>
                    <Select
                      label="Dr/Cr"
                      value={form.opening_balance_type || "DR"}
                      onChange={(e) =>
                        setForm((old) => ({
                          ...old,
                          opening_balance_type: e.target.value,
                        }))
                      }
                    >
                      <MenuItem value="DR">Debit (Dr)</MenuItem>
                      <MenuItem value="CR">Credit (Cr)</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <TextField
                  label="Opening Balance Date"
                  placeholder="dd/mm/yyyy"
                  fullWidth
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  value={form.opening_balance_date || ""}
                  error={
                    String(form.opening_balance_date || "").length === 10 &&
                    !dmyToIso(form.opening_balance_date)
                  }
                  helperText={
                    String(form.opening_balance_date || "").length === 10 &&
                    !dmyToIso(form.opening_balance_date)
                      ? "Not a valid date"
                      : "Format: dd/mm/yyyy"
                  }
                  onChange={(e) =>
                    setForm((old) => ({
                      ...old,
                      opening_balance_date: maskDmy(e.target.value),
                    }))
                  }
                />
                <FormControl fullWidth>
                  <InputLabel>Balance Type</InputLabel>
                  <Select
                    label="Balance Type"
                    value={form.balance_type || "BALANCE_ONLY"}
                    onChange={(e) =>
                      setForm((old) => ({
                        ...old,
                        balance_type: e.target.value,
                      }))
                    }
                  >
                    {BALANCE_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {dialog?.mode === "edit" && (
                  <Alert severity="info" sx={{ bgcolor: C.soft }}>
                    If this ledger is already used in a voucher, its opening
                    and current balances are kept as they are.
                  </Alert>
                )}
              </>
            )}

            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={2}
              value={form.description || ""}
              onChange={(e) =>
                setForm((old) => ({ ...old, description: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${C.border}` }}>
          <Button
            onClick={closeDialog}
            disabled={saving}
            variant="outlined"
            sx={outlineBtn}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              !String(form.name || "").trim() ||
              (dialog?.type === "ledger" && !form.group_id)
            }
            onClick={dialog?.type === "group" ? saveGroup : saveLedger}
            sx={{ ...primaryBtn, minWidth: 100 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={5000}
        onClose={() => setNotice((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={notice.severity}
          variant="filled"
          onClose={() => setNotice((old) => ({ ...old, open: false }))}
          sx={{ fontSize: 14, fontWeight: W.normal }}
        >
          {notice.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
